import styled from "@emotion/styled";
import {Button, Dialog} from "@blueprintjs/core";
import {NestedJobStop, SuggestAssignmentValidationError} from "../../../generated/graphql";

export interface SuggestErrorDialogProps {
  isOpen: boolean;
  error: string | null;
  validationErrors: SuggestAssignmentValidationError[];
  stops: NestedJobStop[];
  onClose(): void;
}

const SuggestErrorDialog = ({isOpen, error, validationErrors, stops, onClose}: SuggestErrorDialogProps) => {
  const stopSequenceMap = new Map(stops.map((s) => [s.jobStopId, s.sequence]));
  const jobLevelErrors = validationErrors.filter((ve) => ve.jobStopId === 0);
  const stopLevelErrors = validationErrors.filter((ve) => ve.jobStopId !== 0);
  const hasTimeWindowError = stopLevelErrors.some((ve) => ve.field === "timeWindow");
  const missingFieldErrors = stopLevelErrors.filter(
    (ve) => ve.reason.startsWith("Missing") && ve.field !== "timeWindow"
  );
  const invalidAddressErrors = stopLevelErrors.filter(
    (ve) => ve.field === "address" && !ve.reason.startsWith("Missing")
  );

  // Group missing-field errors by stop so each stop is one line listing its missing fields
  const missingByStop = missingFieldErrors.reduce((acc, ve) => {
    const group = acc.get(ve.jobStopId) ?? [];
    group.push(ve);
    acc.set(ve.jobStopId, group);
    return acc;
  }, new Map<number, SuggestAssignmentValidationError[]>());

  return (
    <StyledDialog
      data-testid="suggest-error-dialog"
      isOpen={isOpen}
      onClose={onClose}
      canOutsideClickClose={true}
      canEscapeKeyClose={true}
    >
      <DialogContainer>
        <DialogHeader>
          <DialogTitle data-testid="suggest-error-dialog-title">SUGGESTED ASSIGNMENT UNSUCCESSFUL</DialogTitle>
          <DialogCloseIcon
            variant="minimal"
            data-testid="suggest-error-dialog-close-icon"
            onClick={onClose}
            icon="cross"
          />
        </DialogHeader>
        <DialogBody>
          {error && (
            <DialogText data-testid="suggest-error-message" primary>
              {error}
            </DialogText>
          )}
          <DialogText primary>{"Suggestion isn't possible due to the following issues:"}</DialogText>

          <ErrorSection>
            {jobLevelErrors.length > 0 && (
              <ErrorList data-testid="job-level-errors">
                {jobLevelErrors.map((ve, idx) => (
                  <ErrorItem data-testid="job-error-item" key={idx}>
                    <DialogText primary>{ve.reason}</DialogText>
                  </ErrorItem>
                ))}
              </ErrorList>
            )}

            {hasTimeWindowError && (
              <ErrorList data-testid="time-window-error">
                <ErrorItem>
                  <DialogText primary>Job contains a stop that has an invalid time window.</DialogText>
                </ErrorItem>
              </ErrorList>
            )}

            {missingByStop.size > 0 && (
              <>
                <DialogText primary>{"The job contains stop(s) that missing information:"}</DialogText>
                <ErrorList data-testid="stop-level-errors">
                  {[...missingByStop.entries()].map(([jobStopId, errors]) => (
                    <ErrorItem data-testid="stop-error-item" key={jobStopId}>
                      <DialogText primary>
                        <strong>Stop #{stopSequenceMap.get(jobStopId) ?? jobStopId}</strong>: Missing{" "}
                        {errors.map((ve) => ve.field).join(", ")}
                      </DialogText>
                    </ErrorItem>
                  ))}
                </ErrorList>
              </>
            )}

            {invalidAddressErrors.length > 0 && (
              <>
                <DialogText primary>{"The job contains stop(s) with invalid address:"}</DialogText>
                <ErrorList data-testid="invalid-address-errors">
                  {invalidAddressErrors.map((ve) => (
                    <ErrorItem data-testid="invalid-address-item" key={ve.jobStopId}>
                      <DialogText primary>
                        <strong>Stop #{stopSequenceMap.get(ve.jobStopId) ?? ve.jobStopId}</strong>: {ve.value}
                      </DialogText>
                    </ErrorItem>
                  ))}
                </ErrorList>
              </>
            )}
          </ErrorSection>

          <DialogText primary>{"Please make the necessary adjustments and try again."}</DialogText>
        </DialogBody>
        <Footer>
          <ActionButton data-testid="suggest-error-dialog-close" onClick={onClose}>
            Close
          </ActionButton>
        </Footer>
      </DialogContainer>
    </StyledDialog>
  );
};

export default SuggestErrorDialog;

const StyledDialog = styled(Dialog)`
  width: 485px;
  &.bp5-dialog {
    background-color: #fcfcfc;
    border-radius: 16px;
    font-family: Roboto, sans-serif;
  }
`;

const DialogContainer = styled.div`
  padding: 16px 24px;
`;

const DialogHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DialogTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #797979;
  line-height: 1;
  margin: 0;
`;

const DialogCloseIcon = styled(Button)``;

const DialogBody = styled.div`
  padding: 12px 8px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: right;
  gap: 10px;
`;

const ActionButton = styled(Button)`
  &.bp5-button:not([class*="bp5-intent-"]) {
    padding: 6px 14px;
    font-size: 14px;
    background-color: #f8f8f8;
    box-shadow: 0 1px 1px 0 #00000040;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const DialogText = styled.span<{primary?: boolean}>`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.primary ? "#161616" : "#797979")};
`;

const ErrorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorList = styled.ul`
  padding-left: 20px;
  margin: 0;
`;

const ErrorItem = styled.li`
  list-style-type: disc;
  padding: 2px 0;
`;
